import React, { useState } from 'react';
import { useNavigate,Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FormField } from '../../molecules/FormField';
import Button from '../../atoms/Button';
import Card from '../../atoms/Card';
import { useAuth } from '../../../contexts/AuthContext';
import { LoginRequest } from '../../../types/api';
import { validateLoginForm } from '../../../utils/validation';
import SSOLoginOptions from '../SSOLoginOptions/SSOLoginOptions';
import './LoginForm.scss';

export interface LoginFormProps {
  onSuccess?: (token: string) => void;
  onError?: (error: string) => void;
  className?: string;
}

const LoginForm: React.FC<LoginFormProps> = ({
  onSuccess,
  onError,
  className = '',
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState<LoginRequest>({
    username_or_email: '',
    password: '',
  });
  const [errors, setErrors] = useState<Partial<LoginRequest>>({});
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (field: keyof LoginRequest) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { value } = event.target;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const validationErrors = validateLoginForm({
      email: formData.username_or_email,
      password: formData.password
    }, t);

    // Map validation errors to form field names
    const newErrors: Partial<LoginRequest> = {};
    if (validationErrors.email) {
      newErrors.username_or_email = validationErrors.email;
    }
    if (validationErrors.password) {
      newErrors.password = validationErrors.password;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      // Only trim: usernames can contain capitals and the backend matches
      // case-insensitively, so lowercasing here is unnecessary.
      await login(formData.username_or_email.trim(), formData.password);
      onSuccess?.('Login successful');
      
      // Get the current user to determine redirect path
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Redirect based on user role
      if (currentUser.role === 'barman') {
        navigate('/barman');
      } else if (currentUser.role === 'warehouse') {
        navigate('/warehouse');
      } else if (currentUser.role === 'customer') {
        navigate('/events');
      } else if (currentUser.role === 'organizer') {
        navigate('/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          t('auth.login.error');
      
      setErrors({
        username_or_email: errorMessage,
      });
      
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card variant="elevated" padding="lg" className={`login-form ${className}`}>
      <div className="login-form__header">
        <h2 className="login-form__title">{t('auth.login.title')}</h2>
        <p className="login-form__subtitle">
          {t('auth.login.subtitle')}
        </p>
      </div>

      {/* SSO Login Options - Arriba */}
      <SSOLoginOptions
        onSuccess={() => {
          // Get the current user to determine redirect path
          const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
          
          // Redirect based on user role
          if (currentUser.role === 'barman') {
            navigate('/barman');
          } else if (currentUser.role === 'warehouse') {
            navigate('/warehouse');
          } else if (currentUser.role === 'customer') {
            navigate('/events');
          } else if (currentUser.role === 'organizer') {
            navigate('/dashboard');
          } else {
            navigate('/dashboard');
          }
        }}
        onError={(error) => {
          const errorMessage = error?.message || t('auth.login.error');
          setErrors({
            username_or_email: errorMessage,
          });
          onError?.(errorMessage);
        }}
        showDivider={true}
        dividerText={t('auth.sso.dividerText')}
      />

      <form onSubmit={handleSubmit} className="login-form__form">
        <FormField
          label={t('auth.login.usernameOrEmail')}
          type="text"
          name="username_or_email"
          value={formData.username_or_email}
          onChange={handleInputChange('username_or_email')}
          error={errors.username_or_email}
          required
          autoComplete="username"
          autoFocus
          placeholder={t('auth.login.usernameOrEmailExample')}
        />

        <FormField
          label={t('auth.login.password')}
          type="password"
          name="password"
          value={formData.password}
          onChange={handleInputChange('password')}
          error={errors.password}
          required
          autoComplete="current-password"
          placeholder={t('auth.login.passwordExample')}
        />

        <div className="login-form__actions">
          <Button
            type="submit"
            variant="primary"
            size="md"
            fullWidth
            loading={isLoading}
            disabled={isLoading}
          >
            {isLoading ? t('auth.login.signingIn') : t('auth.login.signIn')}
          </Button>
        </div>

       

        <div className="login-page__footer">
            <p className="login-page__forgot-password-text">
              <Link to="/forgot-password" className="login-page__forgot-password-link">
                {t('auth.login.forgotPassword')}
              </Link>
            </p>
            <p className="login-page__register-text">
              {t('auth.login.noAccount')}{' '}<br/>
              <Link to="/register" className="login-page__register-link">
                {t('auth.login.signUp')}
              </Link>
            </p>
            <p className="login-page__home-text">
              <Link to="/events" className="login-page__home-link">
                {t('navigation.events')}
              </Link>
            </p>
          </div>
      </form>
    </Card>
  );
};

export default LoginForm; 